import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RoomDetailLobby, { resolveWaitingMediaUrl } from '../../components/RoomDetailLobby';
import VideoInitializeTimerModal from '../../modals/VideoInitializeTimerModal';
import { ROUTE_ENUM } from '../../routes/RoutesConstant';
import { logger } from '../../utils/logger';
import { stashRapFireNextQuestion } from '../../utils/rapFirePendingQuestion';
import { rapFireStartGame, startRapFireRoomGame } from '../../network/network_callbacks';
import mqttService from '../../services/mqttService';
import { toast } from 'react-toastify';

const GAME_START_COUNTDOWN_MS = 3 * 1000;

const resolveContestId = (room = {}) =>
  room?.contestId || room?.gameId || room?.roomId || room?._id || room?.id || '';

const RapFireRoomCodeShareStartGame = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { roomName, roomType, createdRoomData, selectedGame, waitingMedia } = location.state || {};

  const initialContestId = resolveContestId(createdRoomData) || resolveContestId(selectedGame);
  const [createdRoomCode, setCreatedRoomCode] = useState(
    createdRoomData?.roomCode || selectedGame?.roomCode || ''
  );
  const [contestId, setContestId] = useState(initialContestId);
  const [displayRoomName, setDisplayRoomName] = useState(
    createdRoomData?.roomName || selectedGame?.roomName || roomName || ''
  );
  const [displayRoomType, setDisplayRoomType] = useState(
    createdRoomData?.roomType || selectedGame?.roomType || roomType || ''
  );
  const [joinedUsersList, setJoinedUsersList] = useState(
    createdRoomData?.joinedUserArr ||
      createdRoomData?.joinedUsers ||
      selectedGame?.joinedUserArr ||
      selectedGame?.joinedUsers ||
      []
  );
  const [isGameStartCountdownOpen, setIsGameStartCountdownOpen] = useState(false);
  const [waitingMediaUrl, setWaitingMediaUrl] = useState(
    resolveWaitingMediaUrl(createdRoomData, selectedGame)
  );
  const pendingNextGameRef = useRef(null);
  const contestIdRef = useRef(contestId);
  contestIdRef.current = contestId;
  const isGameStartCountdownOpenRef = useRef(isGameStartCountdownOpen);
  isGameStartCountdownOpenRef.current = isGameStartCountdownOpen;

  const navigateToLiveGame = useCallback(() => {
    const nextGame = pendingNextGameRef.current;
    const liveContestId = nextGame?.contestId || nextGame?.gameId;
    if (!liveContestId) return;
    setIsGameStartCountdownOpen(false);
    navigate(ROUTE_ENUM.RAP_FIRE_GAME.routePath, {
      replace: true,
      state: { contestId: liveContestId, nextGame: { ...nextGame, gameId: liveContestId } },
    });
  }, [navigate]);

  const beginGameStartCountdown = useCallback((nextContestId) => {
    if (!nextContestId || isGameStartCountdownOpen) return;
    pendingNextGameRef.current = { contestId: nextContestId, gameId: nextContestId };
    setIsGameStartCountdownOpen(true);

    try {
      mqttService.disconnect();
      mqttService.connect('game/room/', nextContestId, {
        next_question: (payload) => {
          logger.log('MQTT next_question (pre-live stash):', payload);
          stashRapFireNextQuestion(payload);
        },
        error: (err) => {
          console.error('MQTT error during game-start countdown:', err);
        },
      });
    } catch (error) {
      console.error('MQTT connect error during game-start countdown:', error);
    }
  }, [isGameStartCountdownOpen]);

  const beginGameStartCountdownRef = useRef(beginGameStartCountdown);
  beginGameStartCountdownRef.current = beginGameStartCountdown;

  useEffect(() => {
    logger.log('Rapchick RapFireRoomCodeShareStartGame');
    const activeContestId = contestIdRef.current || initialContestId;
    if (!activeContestId) {
      toast.error('Contest id missing. Please create or open a room again.');
      return undefined;
    }

    if (!contestId) {
      setContestId(activeContestId);
    }

    mqttService.disconnect();
    loadRapFireGameRoom(activeContestId, { silent: false });
    connectMQTTAndGetJoinedUsersList(activeContestId);

    const refreshIntervalId = setInterval(() => {
      if (isGameStartCountdownOpenRef.current) return;
      loadRapFireGameRoom(contestIdRef.current || activeContestId, { silent: true });
    }, 10000);

    return () => {
      clearInterval(refreshIntervalId);
      mqttService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, initialContestId]);

  const connectMQTTAndGetJoinedUsersList = (mqttContestId) => {
    const activeContestId = mqttContestId || contestIdRef.current;
    try {
      logger.log('Connecting to MQTT for Contest ID : ', activeContestId);
      mqttService.connect('rapFire/room/', activeContestId, {
        'user-join': (payload) => {
          const joinedUsers = payload?.joinedUsers || payload?.joinedUserArr || [];
          setJoinedUsersList(joinedUsers);
        },
        start_game: (payload) => {
          beginGameStartCountdownRef.current(
            payload?.contestId || payload?.gameId || payload?.roomId || activeContestId
          );
        },
        error: (err) => {
          console.error('MQTT error:', err);
        },
        complete: () => {
          logger.log('MQTT subscription complete');
        },
      });
    } catch (error) {
      console.error('MQTT connection error:', error);
    }
  };

  const loadRapFireGameRoom = async (loadContestId, { silent = false } = {}) => {
    const activeContestId = loadContestId || contestIdRef.current;
    if (!activeContestId) return null;
    try {
      logger.log('Loading RapFire game room details for Contest ID : ', activeContestId);
      const response = await startRapFireRoomGame(activeContestId);
      const data = response?.data?.data;
      const status = response?.data?.responseCode;

      logger.log('✅ Successfully loadRapFireGameRoom : ', data);

      if (status === 200 || status === 201 || data) {
        const resolvedContestId = resolveContestId(data) || activeContestId;
        setContestId(resolvedContestId);
        setDisplayRoomName(data?.roomName || '');
        setDisplayRoomType(data?.roomType || '');
        setCreatedRoomCode(data?.roomCode || data?.code || '');
        setJoinedUsersList(
          data?.joinedUserArr || data?.joinedUsers || data?.userArr || []
        );
        const nextMediaUrl = resolveWaitingMediaUrl(data);
        if (nextMediaUrl) setWaitingMediaUrl(nextMediaUrl);
      } else if (!silent) {
        toast.error('Failed to load room. Please try again.');
      }
      return response;
    } catch (error) {
      console.error('❌ Failed to loadRapFireGameRoom : ', error);
      if (!silent) {
        toast.error('Failed to load room. Please try again.');
      }
    }
  };

  const handleCopyCode = () => {
    if (!createdRoomCode) return;
    navigator.clipboard.writeText(`${createdRoomCode}`);
    toast.success('Room Code copied to clipboard!');
  };

  const handleStartGame = async () => {
    logger.log('Start Game Clicked:', displayRoomName, displayRoomType);
    if (!contestId) {
      toast.error('Room not ready. Please try again.');
      return;
    }
    try {
      const response = await rapFireStartGame(contestId);
      const status = response?.data?.responseCode;
      const errorCode = response?.responseCode;
      if (errorCode === 500 || (status && status !== 200 && status !== 201)) {
        toast.error(
          response?.error?.message || response?.data?.error?.message || 'Failed to start game'
        );
        return;
      }
      const nextContestId =
        response?.data?.data?.contestId ||
        response?.data?.data?.gameId ||
        contestId;
      beginGameStartCountdown(nextContestId);
    } catch (error) {
      console.error('❌ Failed to rapFireStartGame : ', error);
      toast.error('Failed to start game. Please try again.');
    }
  };

  return (
    <>
      <RoomDetailLobby
        roomName={displayRoomName}
        roomCode={createdRoomCode}
        members={joinedUsersList}
        mediaFile={waitingMedia || null}
        mediaUrl={waitingMediaUrl}
        onBack={() => navigate(ROUTE_ENUM.RAP_FIRE_ROOM_TYPE.routePath)}
        onCopyCode={handleCopyCode}
        onStartGame={handleStartGame}
        startDisabled={!contestId || isGameStartCountdownOpen}
      />
      <VideoInitializeTimerModal
        open={isGameStartCountdownOpen}
        isGameLoading={isGameStartCountdownOpen}
        initialResumeTimerMs={GAME_START_COUNTDOWN_MS}
        onCountdownComplete={navigateToLiveGame}
        variant="fire"
      />
    </>
  );
};

export default RapFireRoomCodeShareStartGame;
